import React, { useEffect, useState } from "react";
import { erc20Abi, parseUnits, formatUnits } from "viem";
import { useAccount, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { Coins } from "lucide-react";
import { getMultiBurnConfig, getBurnTokens, verifyTokenBurn } from "@/api";
import { cronosMainnet } from "@/wagmi";
import { useApp } from "@/App";
import { TokenPicker } from "./TokenPicker";
import { BurnQuote } from "./BurnQuote";
import { useBurnQuote } from "./useBurnQuote";

const BURN_ABI = [{ type: "function", name: "burn", stateMutability: "nonpayable",
  inputs: [{ name: "token", type: "address" }, { name: "amount", type: "uint256" }], outputs: [] }];

const retryVerify = async (address, hash, tries = 4) => {
  try { return await verifyTokenBurn(address, hash); } catch (e) {
    const s = e?.response?.status;
    if (tries > 1 && (s === 404 || s === 502 || s === 503)) { await new Promise((r) => setTimeout(r, 4000)); return retryVerify(address, hash, tries - 1); }
    throw e;
  }
};

export function BurnAnyCoin({ onCredited }) {
  const { address } = useAccount();
  const { showToast } = useApp();
  const [cfg, setCfg] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [token, setToken] = useState(null);
  const [amount, setAmount] = useState("");
  const { quote, err, loading } = useBurnQuote(token?.address, amount);
  const decimals = quote?.decimals;
  const { writeContract, data: hash, isPending, error, reset } = useWriteContract();
  const receipt = useWaitForTransactionReceipt({ hash });
  const [step, setStep] = useState(null);

  useEffect(() => { getMultiBurnConfig().then(setCfg); getBurnTokens().then(setTokens).catch(() => {}); }, []);
  const reads = useReadContracts({ contracts: token && cfg?.contract ? [
    { address: token.address, abi: erc20Abi, functionName: "balanceOf", args: [address], chainId: cronosMainnet.id },
    { address: token.address, abi: erc20Abi, functionName: "allowance", args: [address, cfg.contract], chainId: cronosMainnet.id },
  ] : [], query: { enabled: !!(token && cfg?.contract && address) } });
  const balance = reads.data?.[0]?.result;
  const allowance = reads.data?.[1]?.result;
  const wei = decimals != null && Number(amount) > 0 ? parseUnits(String(amount), decimals) : 0n;
  const needsApprove = allowance != null && wei > 0n && allowance < wei;

  useEffect(() => {
    if (!receipt.isSuccess || !hash) return;
    if (step === "approve") { reads.refetch(); setStep(null); reset(); showToast("Approved. Now press Burn"); return; }
    if (step === "burn") {
      setStep("verify");
      retryVerify(address, hash)
        .then((r) => { showToast(`Burned ${r.token_amount} ${r.symbol} → +${r.credits_awarded} credits`); onCredited?.(); reads.refetch(); })
        .catch((e) => showToast(e?.response?.data?.detail || "Verification failed", "error"))
        .finally(() => { setStep(null); reset(); });
    }
  }, [receipt.isSuccess]); // eslint-disable-line

  const act = () => {
    if (!wei) return;
    if (balance != null && balance < wei) return showToast("Not enough balance", "error");
    if (needsApprove) { setStep("approve"); writeContract({ address: token.address, abi: erc20Abi, functionName: "approve", args: [cfg.contract, wei], chainId: cronosMainnet.id }); }
    else { setStep("burn"); writeContract({ address: cfg.contract, abi: BURN_ABI, functionName: "burn", args: [token.address, wei], chainId: cronosMainnet.id }); }
  };

  const busy = isPending || receipt.isLoading || step === "verify";
  const sym = quote?.symbol || token?.symbol || "";
  const label = !cfg?.enabled ? "Coming soon" : isPending ? "Sign in wallet…" : receipt.isLoading ? "Confirming…" : step === "verify" ? "Verifying…" : needsApprove ? `Approve ${sym}` : `Burn ${sym}`;

  return (
    <div className="card-tile mt-6" data-testid="burn-any-coin">
      <h2 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2"><Coins className="text-cyan-400 shrink-0" /> Burn any coin → Earn credits</h2>
      <p className="text-sm text-slate-400 mt-1">Burn any Cronos coin with a market price. You earn credits based on its dollar value, plus a bonus for bigger, more popular coins (up to 1.5x). 99% goes to <code>0x…dEaD</code> and 1% to the treasury. This is <b>irreversible</b>.</p>
      {!cfg?.enabled && <div className="mt-3 text-sm text-amber-400" data-testid="burn-any-coming-soon">Opening soon: the new burn contract goes live once the new treasury is set up. You can check prices now.</div>}
      <div className="mt-4"><TokenPicker tokens={tokens} value={token} onChange={setToken} /></div>
      {token && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <input data-testid="burn-any-amount" type="number" inputMode="decimal" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Amount of ${sym}`}
              className="bg-[#12131C] border border-[#2A2E45] rounded-xl px-4 py-3 w-full sm:w-48 font-mono" />
            <button data-testid="burn-any-button" onClick={act} disabled={!cfg?.enabled || busy || !quote?.accepted} className="btn-tactile btn-flame w-full sm:w-auto">{label}</button>
            {balance != null && decimals != null && <span className="text-xs text-slate-400" data-testid="burn-any-balance">Balance: {Number(formatUnits(balance, decimals)).toLocaleString()} {sym}</span>}
          </div>
          <BurnQuote quote={quote} err={err} loading={loading} />
          {error && <div className="text-rose-400 text-sm">{error.shortMessage || error.message}</div>}
        </div>
      )}
    </div>
  );
}
